import React, { useMemo, useState, createElement } from 'react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Search, Filter, Clock, CheckCircle, XCircle, AlertCircle, Calendar, MessageSquare, Image as ImageIcon, FileText, Edit, Building2, Send, HomeIcon, PlusCircle, Pencil, DollarSign } from 'lucide-react';
type RequestStatus = 'pending' | 'in-progress' | 'approved' | 'rejected';
type RequestType = 'new-model' | 'new-option' | 'image-update' | 'details-update' | 'remove-option' | 'update-model' | 'pricing-update' | 'general';
type Request = {
  id: string;
  builderId: string;
  builderName: string;
  projectName: string;
  itemName: string;
  category: string;
  type: RequestType;
  status: RequestStatus;
  description: string;
  submittedDate: string;
  updatedDate: string;
  adminNotes?: string;
};
const requests: Request[] = [{
  id: 'REQ-001',
  builderId: 'BLD-001',
  builderName: 'Mattamy Homes',
  projectName: 'Oakwood Estates',
  itemName: 'Black Stainless Fridge',
  category: 'Interior > Kitchen > Refrigerators',
  type: 'image-update',
  status: 'approved',
  description: 'Need updated product image with new model',
  submittedDate: '2024-01-20',
  updatedDate: '2024-01-22',
  adminNotes: 'Image updated successfully. New photo from manufacturer.'
}, {
  id: 'REQ-002',
  builderId: 'BLD-001',
  builderName: 'Mattamy Homes',
  projectName: 'Oakwood Estates',
  itemName: 'Gray Asphalt Shingles',
  category: 'Exterior > Roofing > Shingles',
  type: 'details-update',
  status: 'in-progress',
  description: 'Update warranty information from 25 to 30 years',
  submittedDate: '2024-01-21',
  updatedDate: '2024-01-21'
}, {
  id: 'REQ-003',
  builderId: 'BLD-002',
  builderName: 'Brookfield Residential',
  projectName: 'Riverside Commons',
  itemName: 'White Vinyl Siding',
  category: 'Exterior > Siding > Vinyl',
  type: 'remove-option',
  status: 'pending',
  description: 'Discontinued by manufacturer, need to remove from options',
  submittedDate: '2024-01-22',
  updatedDate: '2024-01-22'
}, {
  id: 'REQ-004',
  builderId: 'BLD-001',
  builderName: 'Mattamy Homes',
  projectName: 'Maple Residences',
  itemName: 'Natural Oak Flooring',
  category: 'Interior > Flooring > Hardwood',
  type: 'pricing-update',
  status: 'rejected',
  description: 'Change price from $8,500 to $9,200',
  submittedDate: '2024-01-19',
  updatedDate: '2024-01-20',
  adminNotes: 'Price increase not approved. Please provide manufacturer documentation.'
}, {
  id: 'REQ-005',
  builderId: 'BLD-003',
  builderName: 'Pulte Homes',
  projectName: 'Sunset Ridge',
  itemName: 'The Lawson',
  category: 'Models',
  type: 'new-model',
  status: 'in-progress',
  description: 'Add new Lawson model to the community with 3 elevations',
  submittedDate: '2024-01-22',
  updatedDate: '2024-01-22'
}, {
  id: 'REQ-006',
  builderId: 'BLD-002',
  builderName: 'Brookfield Residential',
  projectName: 'Riverside Commons',
  itemName: 'Smart Home Package',
  category: 'Packages',
  type: 'new-option',
  status: 'pending',
  description: 'Create a new smart home bundle including thermostat, doorbell, and locks',
  submittedDate: '2024-01-23',
  updatedDate: '2024-01-23'
}, {
  id: 'REQ-007',
  builderId: 'BLD-001',
  builderName: 'Mattamy Homes',
  projectName: 'Oakwood Estates',
  itemName: 'General Inquiry',
  category: 'General',
  type: 'general',
  status: 'pending',
  description: 'Can we get a training session for our new sales agents next week?',
  submittedDate: '2024-01-24',
  updatedDate: '2024-01-24'
}];
const statusConfig = {
  pending: {
    label: 'Pending',
    variant: 'warning' as const,
    icon: Clock,
    color: 'text-amber-600'
  },
  'in-progress': {
    label: 'In Progress',
    variant: 'primary' as const,
    icon: AlertCircle,
    color: 'text-blue-600'
  },
  approved: {
    label: 'Completed',
    variant: 'success' as const,
    icon: CheckCircle,
    color: 'text-green-600'
  },
  rejected: {
    label: 'Rejected',
    variant: 'danger' as const,
    icon: XCircle,
    color: 'text-red-600'
  }
};
const typeConfig = {
  'new-model': {
    label: 'Add New Model',
    icon: HomeIcon,
    color: 'text-teal-600'
  },
  'new-option': {
    label: 'Add New Option',
    icon: PlusCircle,
    color: 'text-indigo-600'
  },
  'image-update': {
    label: 'Image Update',
    icon: ImageIcon,
    color: 'text-purple-600'
  },
  'details-update': {
    label: 'Details Update',
    icon: Edit,
    color: 'text-blue-600'
  },
  'remove-option': {
    label: 'Remove Option',
    icon: XCircle,
    color: 'text-red-600'
  },
  'update-model': {
    label: 'Update Model Details',
    icon: Pencil,
    color: 'text-orange-600'
  },
  'pricing-update': {
    label: 'Pricing Update',
    icon: DollarSign,
    color: 'text-green-600'
  },
  general: {
    label: 'General Request',
    icon: MessageSquare,
    color: 'text-slate-600'
  }
};
export function AdminRequestsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | RequestStatus>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | RequestType>('all');
  const [builderFilter, setBuilderFilter] = useState<string>('all');
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseData, setResponseData] = useState({
    status: '' as RequestStatus,
    notes: ''
  });
  // Get unique builders
  const builders = useMemo(() => {
    const uniqueBuilders = Array.from(new Set(requests.map(r => JSON.stringify({
      id: r.builderId,
      name: r.builderName
    })))).map(str => JSON.parse(str));
    return uniqueBuilders;
  }, []);
  // Filtered requests
  const filteredRequests = useMemo(() => {
    let filtered = requests;
    // Builder filter
    if (builderFilter !== 'all') {
      filtered = filtered.filter(r => r.builderId === builderFilter);
    }
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(r => r.type === typeFilter);
    }
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => r.id.toLowerCase().includes(query) || r.builderName.toLowerCase().includes(query) || r.projectName.toLowerCase().includes(query) || r.itemName.toLowerCase().includes(query) || r.category.toLowerCase().includes(query) || r.description.toLowerCase().includes(query));
    }
    return filtered;
  }, [builderFilter, statusFilter, typeFilter, searchQuery]);
  // Stats
  const stats = useMemo(() => {
    const filtered = builderFilter === 'all' ? requests : requests.filter(r => r.builderId === builderFilter);
    return {
      total: filtered.length,
      pending: filtered.filter(r => r.status === 'pending').length,
      inProgress: filtered.filter(r => r.status === 'in-progress').length,
      approved: filtered.filter(r => r.status === 'approved').length,
      rejected: filtered.filter(r => r.status === 'rejected').length
    };
  }, [builderFilter]);
  const handleRespond = (request: Request) => {
    setSelectedRequest(request);
    setShowResponseModal(true);
    setResponseData({
      status: request.status === 'pending' ? 'in-progress' : request.status,
      notes: request.adminNotes || ''
    });
  };
  const handleSubmitResponse = () => {
    console.log('Submitting response:', {
      request: selectedRequest,
      response: responseData
    });
    setShowResponseModal(false);
    setSelectedRequest(null);
  };
  return <>
      <div className="space-y-6 max-w-7xl mx-auto" data-id="element-2165">
        <div className="flex items-center justify-between" data-id="element-2166">
          <div data-id="element-2167">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-2168">
              All Change Requests
            </h1>
            <p className="text-slate-500" data-id="element-2169">
              Review and manage requests from all builders
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-2170">
          <Card noPadding data-id="element-2171">
            <div className="p-4" data-id="element-2172">
              <div className="flex items-center justify-between mb-2" data-id="element-2173">
                <p className="text-sm text-slate-500" data-id="element-2174">Total Requests</p>
                <FileText className="h-4 w-4 text-slate-400" data-id="element-2175" />
              </div>
              <p className="text-2xl font-bold text-slate-900" data-id="element-2176">{stats.total}</p>
            </div>
          </Card>

          <Card noPadding data-id="element-2177">
            <div className="p-4" data-id="element-2178">
              <div className="flex items-center justify-between mb-2" data-id="element-2179">
                <p className="text-sm text-slate-500" data-id="element-2180">Pending</p>
                <Clock className="h-4 w-4 text-amber-500" data-id="element-2181" />
              </div>
              <p className="text-2xl font-bold text-amber-600" data-id="element-2182">
                {stats.pending}
              </p>
            </div>
          </Card>

          <Card noPadding data-id="element-2183">
            <div className="p-4" data-id="element-2184">
              <div className="flex items-center justify-between mb-2" data-id="element-2185">
                <p className="text-sm text-slate-500" data-id="element-2186">In Progress</p>
                <AlertCircle className="h-4 w-4 text-blue-500" data-id="element-2187" />
              </div>
              <p className="text-2xl font-bold text-blue-600" data-id="element-2188">
                {stats.inProgress}
              </p>
            </div>
          </Card>

          <Card noPadding data-id="element-2189">
            <div className="p-4" data-id="element-2190">
              <div className="flex items-center justify-between mb-2" data-id="element-2191">
                <p className="text-sm text-slate-500" data-id="element-2192">Completed</p>
                <CheckCircle className="h-4 w-4 text-green-500" data-id="element-2193" />
              </div>
              <p className="text-2xl font-bold text-green-600" data-id="element-2194">
                {stats.approved}
              </p>
            </div>
          </Card>

          <Card noPadding data-id="element-2195">
            <div className="p-4" data-id="element-2196">
              <div className="flex items-center justify-between mb-2" data-id="element-2197">
                <p className="text-sm text-slate-500" data-id="element-2198">Rejected</p>
                <XCircle className="h-4 w-4 text-red-500" data-id="element-2199" />
              </div>
              <p className="text-2xl font-bold text-red-600" data-id="element-2200">
                {stats.rejected}
              </p>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card noPadding data-id="element-2201">
          <div className="p-4 border-b border-slate-200 bg-slate-50 rounded-t-xl" data-id="element-2202">
            <div className="flex items-center gap-3 mb-3" data-id="element-2203">
              {/* Search */}
              <div className="flex-1 relative" data-id="element-2204">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-2205" />
                <input type="text" placeholder="Search by ID, builder, community, item, or description..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2206" />
              </div>
            </div>

            <div className="flex items-center gap-3" data-id="element-2207">
              {/* Builder Filter */}
              <select value={builderFilter} onChange={e => setBuilderFilter(e.target.value)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-2208">
                <option value="all" data-id="element-2209">All Builders</option>
                {builders.map(builder => <option key={builder.id} value={builder.id} data-id="element-2210">
                    {builder.name}
                  </option>)}
              </select>

              {/* Status Filter */}
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-2211">
                <option value="all" data-id="element-2212">All Status</option>
                <option value="pending" data-id="element-2213">Pending</option>
                <option value="in-progress" data-id="element-2214">In Progress</option>
                <option value="approved" data-id="element-2215">Completed</option>
                <option value="rejected" data-id="element-2216">Rejected</option>
              </select>

              {/* Type Filter */}
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-2217">
                <option value="all" data-id="element-2218">All Types</option>
                {(Object.keys(typeConfig) as RequestType[]).map(type => <option key={type} value={type} data-id="element-2219">
                    {typeConfig[type].label}
                  </option>)}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-2220">
            <table className="w-full text-left text-sm" data-id="element-2221">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-2222">
                <tr data-id="element-2223">
                  <th className="px-6 py-4" data-id="element-2224">Request ID</th>
                  <th className="px-6 py-4" data-id="element-2225">Builder</th>
                  <th className="px-6 py-4" data-id="element-2226">Community</th>
                  <th className="px-6 py-4" data-id="element-2227">Item</th>
                  <th className="px-6 py-4" data-id="element-2228">Type</th>
                  <th className="px-6 py-4" data-id="element-2229">Status</th>
                  <th className="px-6 py-4" data-id="element-2230">Submitted</th>
                  <th className="px-6 py-4 text-right" data-id="element-2231">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2232">
                {filteredRequests.length === 0 ? <tr data-id="element-2233">
                    <td colSpan={8} className="px-6 py-12 text-center text-slate-500" data-id="element-2234">
                      No requests found matching your filters
                    </td>
                  </tr> : filteredRequests.map(request => {
                const StatusIcon = statusConfig[request.status].icon;
                const TypeIcon = typeConfig[request.type].icon;
                return <tr key={request.id} className="hover:bg-slate-50 transition-colors" data-id="element-2235">
                        <td className="px-6 py-4 font-medium text-slate-900" data-id="element-2236">
                          {request.id}
                        </td>
                        <td className="px-6 py-4" data-id="element-2237">
                          <div className="flex items-center gap-2" data-id="element-2238">
                            <Building2 className="h-4 w-4 text-slate-400" data-id="element-2239" />
                            <span className="text-slate-700" data-id="element-2240">
                              {request.builderName}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-600" data-id="element-2241">
                          {request.projectName}
                        </td>
                        <td className="px-6 py-4" data-id="element-2242">
                          <div className="font-medium text-slate-900" data-id="element-2243">
                            {request.itemName}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-2244">
                            {request.category}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-2245">
                          <div className="flex items-center gap-2" data-id="element-2246">
                            <TypeIcon className={`h-4 w-4 ${typeConfig[request.type].color}`} data-id="element-2247" />
                            <span className="text-slate-700 text-xs" data-id="element-2248">
                              {typeConfig[request.type].label}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-2249">
                          <Badge variant={statusConfig[request.status].variant} data-id="element-2250">
                            <div className="flex items-center gap-1" data-id="element-2251">
                              <StatusIcon className="h-3 w-3" data-id="element-2252" />
                              {statusConfig[request.status].label}
                            </div>
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-slate-600" data-id="element-2253">
                          <div className="flex items-center gap-1" data-id="element-2254">
                            <Calendar className="h-3 w-3 text-slate-400" data-id="element-2255" />
                            {new Date(request.submittedDate).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-2256">
                          <div className="flex items-center justify-end gap-2" data-id="element-2257">
                            <Button variant="ghost" size="sm" onClick={() => setSelectedRequest(request)} data-id="element-2258">
                              View
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleRespond(request)} data-id="element-2259">
                              Respond
                            </Button>
                          </div>
                        </td>
                      </tr>;
              })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Request Details Modal */}
      {selectedRequest && !showResponseModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-2260">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" data-id="element-2261">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white" data-id="element-2262">
              <div data-id="element-2263">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-2264">
                  Request Details
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-2265">
                  {selectedRequest.id}
                </p>
              </div>
              <button onClick={() => setSelectedRequest(null)} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-2266">
                <XCircle className="h-5 w-5" data-id="element-2267" />
              </button>
            </div>

            <div className="p-6 space-y-6" data-id="element-2268">
              {/* Status & Type */}
              <div className="flex items-center gap-3" data-id="element-2269">
                <Badge variant={statusConfig[selectedRequest.status].variant} className="text-sm" data-id="element-2270">
                  <div className="flex items-center gap-1" data-id="element-2271">
                    {createElement(statusConfig[selectedRequest.status].icon, {
                  className: 'h-3 w-3'
                })}
                    {statusConfig[selectedRequest.status].label}
                  </div>
                </Badge>
                <Badge variant="neutral" className="text-sm" data-id="element-2272">
                  <div className="flex items-center gap-1" data-id="element-2273">
                    {createElement(typeConfig[selectedRequest.type].icon, {
                  className: 'h-3 w-3'
                })}
                    {typeConfig[selectedRequest.type].label}
                  </div>
                </Badge>
              </div>

              {/* Builder & Project Info */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3" data-id="element-2274">
                <div className="grid grid-cols-2 gap-4" data-id="element-2275">
                  <div data-id="element-2276">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-2277">Builder</p>
                    <div className="flex items-center gap-2" data-id="element-2278">
                      <Building2 className="h-4 w-4 text-slate-400" data-id="element-2279" />
                      <p className="font-medium text-slate-900" data-id="element-2280">
                        {selectedRequest.builderName}
                      </p>
                    </div>
                  </div>
                  <div data-id="element-2281">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-2282">Community</p>
                    <p className="font-medium text-slate-900" data-id="element-2283">
                      {selectedRequest.projectName}
                    </p>
                  </div>
                </div>
                <div data-id="element-2284">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-2285">Item Name</p>
                  <p className="font-medium text-slate-900" data-id="element-2286">
                    {selectedRequest.itemName}
                  </p>
                </div>
                <div data-id="element-2287">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-2288">Category Path</p>
                  <p className="text-sm text-slate-700" data-id="element-2289">
                    {selectedRequest.category}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div data-id="element-2290">
                <p className="text-sm font-medium text-slate-700 mb-2" data-id="element-2291">
                  Description
                </p>
                <div className="bg-slate-50 rounded-lg p-4" data-id="element-2292">
                  <p className="text-slate-700" data-id="element-2293">
                    {selectedRequest.description}
                  </p>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4" data-id="element-2294">
                <div data-id="element-2295">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-2296">Submitted Date</p>
                  <div className="flex items-center gap-2 text-slate-700" data-id="element-2297">
                    <Calendar className="h-4 w-4 text-slate-400" data-id="element-2298" />
                    {new Date(selectedRequest.submittedDate).toLocaleDateString()}
                  </div>
                </div>
                <div data-id="element-2299">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-2300">Last Updated</p>
                  <div className="flex items-center gap-2 text-slate-700" data-id="element-2301">
                    <Clock className="h-4 w-4 text-slate-400" data-id="element-2302" />
                    {new Date(selectedRequest.updatedDate).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              {selectedRequest.adminNotes && <div data-id="element-2303">
                  <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2" data-id="element-2304">
                    <MessageSquare className="h-4 w-4" data-id="element-2305" />
                    Admin Notes
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2306">
                    <p className="text-sm text-blue-900" data-id="element-2307">
                      {selectedRequest.adminNotes}
                    </p>
                  </div>
                </div>}
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3" data-id="element-2308">
              <Button variant="outline" onClick={() => setSelectedRequest(null)} data-id="element-2309">
                Close
              </Button>
              <Button onClick={() => handleRespond(selectedRequest)} data-id="element-2310">
                Respond to Request
              </Button>
            </div>
          </div>
        </div>}

      {/* Response Modal */}
      {showResponseModal && selectedRequest && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-2311">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4" data-id="element-2312">
            <div className="p-6 border-b border-slate-200" data-id="element-2313">
              <h2 className="text-xl font-bold text-slate-900" data-id="element-2314">
                Respond to Request
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-2315">
                {selectedRequest.id} - {selectedRequest.itemName}
              </p>
            </div>

            <div className="p-6 space-y-4" data-id="element-2316">
              <div data-id="element-2317">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2318">
                  Update Status
                </label>
                <select value={responseData.status} onChange={e => setResponseData({
              ...responseData,
              status: e.target.value as RequestStatus
            })} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-2319">
                  <option value="" data-id="element-2320">Select status...</option>
                  <option value="in-progress" data-id="element-2321">In Progress</option>
                  <option value="approved" data-id="element-2322">Completed</option>
                  <option value="rejected" data-id="element-2323">Rejected</option>
                </select>
              </div>

              <div data-id="element-2324">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2325">
                  Admin Notes
                </label>
                <textarea value={responseData.notes} onChange={e => setResponseData({
              ...responseData,
              notes: e.target.value
            })} placeholder="Add notes for the builder..." className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" rows={4} data-id="element-2326" />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-2327">
                <p className="text-xs text-blue-800" data-id="element-2328">
                  The builder will be notified of your response and can view
                  your notes.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3" data-id="element-2329">
              <Button variant="outline" onClick={() => setShowResponseModal(false)} data-id="element-2330">
                Cancel
              </Button>
              <Button onClick={handleSubmitResponse} disabled={!responseData.status} leftIcon={<Send className="h-4 w-4" data-id="element-2332" />} data-id="element-2331">
                Send Response
              </Button>
            </div>
          </div>
        </div>}
    </>;
}